import type { CSSProperties, Ref } from 'vue';

/**
 * Tracks an element's viewport bounds for fixed preview overlays (badge + outline).
 */
export function useElementOverlayPosition(
  target: Ref<Element | null | undefined>
) {
  const bounds = ref<DOMRect | null>(null);

  let resizeObserver: ResizeObserver | undefined;
  let frameId: number | undefined;

  const updateBounds = () => {
    const element = target.value;

    if (!element) {
      bounds.value = null;
      return;
    }

    bounds.value = element.getBoundingClientRect();
  };

  const scheduleUpdate = () => {
    if (frameId !== undefined) {
      return;
    }

    frameId = requestAnimationFrame(() => {
      frameId = undefined;
      updateBounds();
    });
  };

  const disconnectObserver = () => {
    resizeObserver?.disconnect();
    resizeObserver = undefined;
  };

  watch(
    target,
    (element) => {
      disconnectObserver();

      if (!element) {
        bounds.value = null;
        return;
      }

      resizeObserver = new ResizeObserver(scheduleUpdate);
      resizeObserver.observe(element);
      updateBounds();
    },
    { immediate: true }
  );

  onMounted(() => {
    window.addEventListener('scroll', scheduleUpdate, {
      capture: true,
      passive: true
    });
    window.addEventListener('resize', scheduleUpdate, { passive: true });
  });

  onUnmounted(() => {
    disconnectObserver();

    if (frameId !== undefined) {
      cancelAnimationFrame(frameId);
    }

    window.removeEventListener('scroll', scheduleUpdate, { capture: true });
    window.removeEventListener('resize', scheduleUpdate);
  });

  const hasAnchor = computed(() => hasVisibleBounds(bounds.value));

  const outlineStyle = computed((): CSSProperties | undefined => {
    const rect = bounds.value;

    if (!hasVisibleBounds(rect)) {
      return undefined;
    }

    return toOverlayPosition(rect);
  });

  const badgeStyle = computed((): CSSProperties | undefined => {
    const rect = bounds.value;

    if (!hasVisibleBounds(rect)) {
      return undefined;
    }

    return {
      top: `${rect.top + 8}px`,
      left: `${rect.left + 8}px`,
      'z-index': 5
    };
  });

  return {
    badgeStyle,
    hasAnchor,
    outlineStyle
  };
}

const toOverlayPosition = (rect: DOMRect): CSSProperties => ({
  top: `${rect.top}px`,
  left: `${rect.left}px`,
  width: `${rect.width}px`,
  height: `${rect.height}px`,
  'z-index': 5
});

const hasVisibleBounds = (rect: DOMRect | null): rect is DOMRect =>
  rect !== null && rect.width > 0 && rect.height > 0;
